import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowRight, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.2fr_1fr] surface relative overflow-hidden" data-testid="login-page">

      {/* Left — editorial hero with 3D sphere */}
      <div className="relative overflow-hidden hidden lg:flex flex-col justify-between p-14 xl:p-20 border-r hairline-soft">
        {/* 3D spheres — Trusdo style */}
        <div className="sphere w-[520px] h-[520px] top-[-160px] right-[-140px] opacity-80 animate-sphere-float"/>
        <div className="sphere w-[180px] h-[180px] bottom-[80px] left-[-40px] opacity-50 animate-sphere-float-2"/>
        <div className="absolute inset-0 grain pointer-events-none"/>

        {/* Header row */}
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg border hairline surface-2 grid place-items-center">
              <span className="ink font-black text-lg leading-none">O</span>
            </div>
            <div className="tiny-label">Project · Owl</div>
          </div>
          <div className="section-num">.01</div>
        </div>

        {/* Center headline */}
        <div className="relative max-w-2xl">
          <div className="section-eyebrow ink-subtle mb-6">I run</div>
          <h1 className="display-heading text-6xl xl:text-8xl leading-[0.94] ink uppercase">
            Bars,<br/>
            <span className="display-serif italic normal-case font-normal">clubs</span> &amp; lounges<br/>
            like one<br/>
            system.
          </h1>
          <div className="mt-8 flex items-center gap-3 ink-subtle">
            <span className="h-px w-10 bg-foreground/25"/>
            <span className="text-[11px] uppercase tracking-[0.28em] font-medium">
              The operating system for nightlife
            </span>
          </div>
        </div>

        {/* Footer row */}
        <div className="relative flex items-end justify-between">
          <div className="flex flex-wrap gap-1.5 max-w-md">
            {["Multi-branch", "POS + KDS", "Reservations", "Events", "Payroll", "Audit"].map((t) => (
              <span key={t} className="text-[9px] uppercase tracking-[0.24em] font-semibold px-2.5 py-1.5 rounded-full border hairline surface-card ink-muted">
                {t}
              </span>
            ))}
          </div>
          <div className="scroll-hint h-32 flex items-end">SCROLL</div>
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-center p-6 sm:p-12 lg:p-16">
        <div className="absolute top-6 right-6"><ThemeToggle/></div>

        <div className="w-full max-w-md mx-auto animate-fade-up">
          <div className="lg:hidden mb-12 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg border hairline surface-2 grid place-items-center">
              <span className="ink font-black text-xl">O</span>
            </div>
            <div className="tiny-label">Project · Owl</div>
          </div>

          <div className="flex items-start justify-between">
            <div>
              <div className="section-eyebrow ink-subtle">Sign in</div>
              <h2 className="mt-2 display-heading text-4xl ink uppercase leading-none">Welcome<br/>back.</h2>
            </div>
            <span className="section-num">.02</span>
          </div>
          <p className="mt-6 ink-muted text-sm max-w-sm leading-relaxed">Access your NightOps command center. Every action is scoped by your role.</p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label htmlFor="login-email" className="section-eyebrow">Email</label>
              <input
                id="login-email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 w-full border-b hairline bg-transparent px-0 py-2 ink placeholder:ink-subtle focus:border-foreground/60 outline-none transition-colors mono text-sm"
                placeholder="you@venue.ph"
                required
              />
            </div>
            <div>
              <label htmlFor="login-password" className="section-eyebrow">Password</label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-12 w-full border-b hairline bg-transparent px-0 pr-10 py-2 ink placeholder:ink-subtle focus:border-foreground/60 outline-none transition-colors mono text-sm"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 ink-muted hover:ink transition-colors mt-1"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-xs px-3 py-2 uppercase tracking-wider" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="pt-4 flex items-center gap-3">
              <button
                type="submit"
                data-testid="login-submit"
                disabled={busy}
                className="btn-pill btn-pill-solid h-12 px-8 flex-1 disabled:opacity-60"
              >
                {busy ? <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin"/> : <>Sign in <ArrowRight size={14}/></>}
              </button>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t hairline-soft">
            <p className="text-[11px] ink-subtle leading-relaxed">
              Sign in using your assigned account. Access is scoped to your organization, branch, and role.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
